import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, MessageCircle, ShieldCheck } from "lucide-react";
import { CONTACT, HERO_IMAGE, ACCREDITATIONS, whatsappLink } from "../../lib/site";
import { MaskLines } from "../site/Reveal";
import { scrollToId } from "../site/SmoothScroll";

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section id="top" ref={ref} className="relative min-h-[100svh] flex items-end overflow-hidden noise-overlay">
      <motion.div style={{ y: imgY, scale }} className="absolute inset-0 z-0">
        <img src={HERO_IMAGE} alt="Modern renovated property exterior at dusk" className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-forest via-forest/80 to-forest/40" />
        <div className="absolute inset-0 bg-forest/30" />
      </motion.div>

      <motion.div style={{ opacity: fade }} className="relative z-10 w-full mx-auto max-w-[1400px] px-5 sm:px-8 pb-16 pt-32">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1, duration: 0.6 }}
          className="inline-flex items-center gap-2 rounded-full border border-brand-orange/40 bg-forest-surface/50 backdrop-blur px-4 py-2 mb-8"
        >
          <span className="h-2 w-2 rounded-full bg-brand-orange animate-pulse" />
          <span className="font-display uppercase tracking-[0.2em] text-xs text-cream/90">{CONTACT.tagline}</span>
        </motion.div>

        <h1 className="font-display uppercase font-600 tracking-tighter leading-[0.92] text-cream text-[15vw] sm:text-[13vw] lg:text-[8.5rem] xl:text-[10rem]">
          <MaskLines lines={["Domestic &", "Commercial"]} />
          <span className="block overflow-hidden">
            <motion.span
              className="block text-brand-orange"
              initial={{ y: "110%" }}
              animate={{ y: "0%" }}
              transition={{ duration: 0.9, delay: 0.39, ease: [0.22, 1, 0.36, 1] }}
            >
              Services
            </motion.span>
          </span>
        </h1>

        <div className="mt-8 grid gap-8 lg:grid-cols-12 items-end">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7, duration: 0.7 }}
            className="lg:col-span-6 text-cream/80 text-base sm:text-lg leading-relaxed max-w-xl"
          >
            We provide reliable, professional, and affordable solutions for all your
            property needs. From small repairs to complete renovations, our skilled
            team ensures quality workmanship and customer satisfaction every time.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.85, duration: 0.7 }}
            className="lg:col-span-6 lg:justify-self-end flex flex-col sm:flex-row gap-3"
          >
            <button
              onClick={() => scrollToId("contact")}
              className="inline-flex items-center justify-center gap-2 h-14 px-8 rounded-full bg-brand-orange hover:bg-brand-orangeHover text-white font-display uppercase tracking-wider text-base transition-[background-color,transform] duration-300 hover:-translate-y-1"
              data-testid="hero-free-quote"
            >
              Get a Free Quote <ArrowDown className="h-5 w-5" />
            </button>
            <a
              href={whatsappLink("Hi Bezalel Property Services, I'd like to get a quote for...")}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 h-14 px-8 rounded-full border border-white/25 bg-white/5 backdrop-blur text-cream hover:bg-white/10 hover:border-white/40 font-display uppercase tracking-wider text-base transition-[background-color,border-color,transform] duration-300 hover:-translate-y-1"
              data-testid="hero-whatsapp"
            >
              <MessageCircle className="h-5 w-5" /> WhatsApp Chat
            </a>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.1, duration: 0.8 }}
          className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-3 border-t border-white/10 pt-6"
        >
          {ACCREDITATIONS.map((a) => (
            <div key={a} className="flex items-center gap-2 text-cream/70">
              <ShieldCheck className="h-4 w-4 text-brand-orange" />
              <span className="font-display uppercase tracking-[0.14em] text-xs">{a}</span>
            </div>
          ))}
        </motion.div>
      </motion.div>
    </section>
  );
};
